import { useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Grid } from '@mui/material';
import DefaultConstants from '../../../../../data/Constants';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import DateField from '../../../../../components/DateField';
import SelectField from '../../../../../components/SelectField';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import { IRoom } from '../../../../../interfaces/IPartnerPms';
import IUser from '../../../../../interfaces/IUser';
import { IDefaultStatusData, ServiceOrderLogTypeData, ServiceOrderLogTypeEnum, ServicePriorityData, ServiceStatusData } from '../../../../../data/Enums';
import useState from '../../../../../hooks/useState';
import TextField from '../../../../../components/TextField';


export interface IServiceOrderLogReportFilters {
    enterprise: IEnterprise | undefined;
    room: IRoom[];
    user: IUser[];
    startDate: string;
    endDate: string;
    action: ServiceOrderLogTypeEnum[];
    initialStatus: IDefaultStatusData[];
    finalStatus: IDefaultStatusData[];
    serviceOrder: string;
}


interface IServiceOrderLogReportDialogFilterProps extends IDefaultDialogFilterProps {
    userList: IUser[];
}

const ServiceOrderLogFilters: React.FunctionComponent<IServiceOrderLogReportDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList,
    userList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();
    
    const state = useState<IServiceOrderLogReportFilters>({
        enterprise: enterprise,
        room: [],
        user: [],
        startDate: DateTimeHelper.Now('YYYY-MM-DD'),
        endDate: DateTimeHelper.Now('YYYY-MM-DD'), 
        action: [],
        initialStatus: [], 
        finalStatus: [],
        serviceOrder: ''
    });


    useEffect(() => {
        if (open) {
            state.reset();
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        state.update('room', []);
        state.update('user', []);
        // eslint-disable-next-line
    }, [state.values.enterprise]);


    const users = useMemo(() => {
        return userList.filter(u => u.enterprises.map(e => e.id).includes(state.values.enterprise?.id ?? 0)) ?? [];
    }, [state.values.enterprise, userList]);


    const serviceOrderLogPossibleStatus = useMemo(() => {
        const action = state.values.action;
        let status = [] as IDefaultStatusData[];

        if (action.length === 0 || action.includes(ServiceOrderLogTypeEnum.UpdateStatus)) {
            status = status.concat(...Object.values(ServiceStatusData));
        }

        if (action.length === 0 || action.includes(ServiceOrderLogTypeEnum.UpdatePriority)) {
            status = status.concat(...Object.values(ServicePriorityData));
        }

        return status;
        // eslint-disable-next-line
    }, [state.values.enterprise, state.values.action]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const _filters = state.values;
        const startDate = _filters.startDate;
        const endDate = _filters.endDate;

        if (startDate && endDate && startDate > endDate) {
            const aux = startDate;
            _filters.startDate = endDate;
            _filters.endDate = aux;
        }

        handleOpenPreview(_filters);

        state.update('startDate', _filters.startDate);
        state.update('endDate', _filters.endDate);
        // eslint-disable-next-line
    }, [state.values]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('serviceOrderLog')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={state.values.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            state.update(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={DateTimeHelper.ToDate(state.values.startDate)}
                        onChange={value => state.update('startDate', DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={DateTimeHelper.ToDate(state.values.endDate)}
                        onChange={value => state.update('endDate', DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <TextField
                        label={translate('serviceOrder')}
                        name='serviceOrder'
                        value={state.values.serviceOrder}
                        onChange={e => state.update(e.target.name, e.target.value.trim())}
                        required={false}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('action')}
                        name='action'
                        value={state.values.action.length > 0 ? state.values.action : ['']}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as string[];

                            const action = state.values.action.length > 0 && values.includes('') ? [] : Object.values(ServiceOrderLogTypeData).filter(t => values.includes(t.key)).map(s => s.key);
                            state.update(name, action);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: '', text: translate('all') }].concat(Object.values(ServiceOrderLogTypeData).map(t => {
                            return {
                                value: t.key,
                                text: translate(t.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12} padding={0} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('initialStatus')}
                        name='initialStatus'
                        value={state.values.initialStatus.length > 0 ? state.values.initialStatus.map(s => +s.key) : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = state.values.initialStatus.length > 0 && values.includes(-1) ? [] : serviceOrderLogPossibleStatus.filter(s => values.includes(+s.key));
                            state.update(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(serviceOrderLogPossibleStatus.map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('finalStatus')}
                        name='finalStatus'
                        value={state.values.finalStatus.length > 0 ? state.values.finalStatus.map(s => +s.key) : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = state.values.finalStatus.length > 0 && values.includes(-1) ? [] : serviceOrderLogPossibleStatus.filter(s => values.includes(+s.key));
                            state.update(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(serviceOrderLogPossibleStatus.map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('user')}
                        name='user'
                        value={state.values.user.length > 0 ? state.values.user.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const user = state.values.user.length > 0 && values.includes(-1) ? [] : users.filter(r => values.includes(+r.id));
                            state.update(name, user);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(users.map(r => {
                            return {
                                value: +r.id,
                                text: r.fullName
                            };
                        }))}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default ServiceOrderLogFilters;